/**
 * Global answer-analysis provider settings card for the admin Answers page.
 *
 * @module AnswerAnalysisProviderSettings
 */

import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAnswerAnalysisSettings } from '@/hooks/useAnswerAnalysisSettings';

const PROVIDER_LABEL_KEYS: Record<string, string> = {
  openai: 'admin.answersManager.openai',
  gemini: 'admin.answersManager.gemini',
  anthropic: 'admin.answersManager.anthropic',
};

function providerLabelKey(provider: string): string {
  return PROVIDER_LABEL_KEYS[provider] ?? 'admin.answersManager.openai';
}

export function AnswerAnalysisProviderSettings() {
  const { t } = useTranslation();
  const {
    settings,
    selectedProvider,
    setSelectedProvider,
    isLoading,
    isSaving,
    isDirty,
    save,
  } = useAnswerAnalysisSettings();

  const allowedProviders = settings?.allowedProviders ?? ['openai', 'gemini', 'anthropic'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-ui flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          {t('admin.answersManager.aiAnalysisModel')}
        </CardTitle>
        <CardDescription className="font-ui">
          {t('admin.answersManager.providerSettingsDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="font-ui text-sm">{t('admin.answersManager.loadingProviderSettings')}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="answer-analysis-provider" className="font-ui">
                {t('admin.answersManager.aiAnalysisModel')}
              </Label>
              <Select
                value={selectedProvider}
                onValueChange={setSelectedProvider}
                disabled={isSaving}
              >
                <SelectTrigger id="answer-analysis-provider">
                  <SelectValue placeholder={t('admin.answersManager.selectModel')} />
                </SelectTrigger>
                <SelectContent>
                  {allowedProviders.map((provider) => (
                    <SelectItem key={provider} value={provider}>
                      <span className="font-ui">{t(providerLabelKey(provider))}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              onClick={save}
              disabled={!isDirty || isSaving}
              className="font-ui sm:w-auto w-full"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('admin.answersManager.savingProvider')}
                </>
              ) : (
                t('admin.answersManager.saveProvider')
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
